import { QueryResult, gql } from '@apollo/client';

import {
  BoolEmployeePref,
  EmployeeJsonPref,
  EmployeePrefDataFragment,
  EmployeePrefsQuery,
  JsonEmployeePref,
  Maybe,
  StringEmployeePref,
  useEmployeePrefsQuery,
} from 'src/generated/mloop-graphql';

import IsoTimestamp from 'src/types/IsoTimestamp';

import { EmployeePrefName } from 'src/utils/api/employee/constants';

import { AllEmployeePref } from './types';

type QueryResultType = Omit<QueryResult, 'data'>;

const EmployeePrefFragment = gql`
  fragment EmployeePrefData on EmployeePref {
    id
    orgId
    prefName
    json
    boolValue
    stringValue
    intValue
    stringArray
    jsonArray
    employeeId
    timestamp
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const EmployeePrefsGqlQuery = gql`
  ${EmployeePrefFragment}
  query EmployeePrefs($id: uuid!, $prefName: String!) {
    employee(id: $id) {
      id
      employeePref(prefName: $prefName) {
        id
        orgId
        employeeId
        prefName
        createdAt
        updatedAt
        ...EmployeePrefData
      }
    }
  }
`;

export function useEmployeePrefs(
  pref: EmployeePrefName | AllEmployeePref,
  employeeId: string
): QueryResult<EmployeePrefsQuery> {
  return useEmployeePrefsQuery({
    variables: {
      id: employeeId,
      prefName: pref,
    },
    skip: !employeeId,
  });
}

// This single fetch intentially fetches all the employee prefrences so we can cache.
export function useEmployeePref(
  pref: EmployeePrefName | AllEmployeePref,
  employeeId: string
): [EmployeePrefDataFragment | null, QueryResultType] {
  const { data, ...rest } = useEmployeePrefs(pref, employeeId);

  return [data?.employee?.employeePref ? data.employee?.employeePref : null, rest];
}

export function useEmployeePrefBool(pref: BoolEmployeePref, employeeId: string): [boolean | null, QueryResultType] {
  const [value, rest] = useEmployeePref(pref, employeeId);
  return [value?.boolValue || null, rest];
}

export function useEmployeePrefString(
  pref: EmployeePrefName | StringEmployeePref,
  employeeId: string,
  defaultValue: string | null = null
): [string | null, QueryResultType] {
  const [value, rest] = useEmployeePref(pref, employeeId);
  return [value?.stringValue || defaultValue, rest];
}

export function useEmployeePrefTimestamp(
  pref: EmployeePrefName,
  employeeId: string,
  defaultValue: string | null = null
): [IsoTimestamp | null, QueryResultType] {
  const [value, rest] = useEmployeePref(pref, employeeId);
  return [value?.timestamp || defaultValue, rest];
}

export function useEmployeePrefInt(
  pref: EmployeePrefName,
  employeeId: string,
  defaultValue: number | null = null
): [number | null, QueryResultType] {
  const [value, rest] = useEmployeePref(pref, employeeId);
  return [value?.intValue || defaultValue, rest];
}

export function useEmployeePrefStringArray(
  pref: EmployeePrefName,
  employeeId: string,
  defaultValue: Maybe<string>[] | null = null
): [Maybe<string>[] | null, QueryResultType] {
  const [value, rest] = useEmployeePref(pref, employeeId);
  return [value?.stringArray || defaultValue, rest];
}

export function useEmployeePrefJson<T>(
  pref: EmployeePrefName,
  employeeId: string,
  defaultValue: T | null = null
): [T | null, QueryResultType] {
  const [value, rest] = useEmployeePref(pref, employeeId);
  if (!value?.json) {
    return [defaultValue, rest];
  }
  return [JSON.parse(value.json) as unknown as T, rest];
}

export function useEmployeePrefJsonTyped<T extends EmployeeJsonPref>(
  pref: JsonEmployeePref,
  employeeId: string,
  defaultValue: T | null = null
): [T | null, QueryResultType] {
  const [value, rest] = useEmployeePref(pref, employeeId);

  const newValue = value?.json ?? defaultValue;
  return [newValue as T, rest];
}

export function useEmployeePrefJsonArray<T>(
  pref: EmployeePrefName,
  employeeId: string,
  defaultValue: T[] | null = null
): [T[] | null, QueryResultType] {
  const [value, rest] = useEmployeePref(pref, employeeId);
  if (!value?.jsonArray) {
    return [defaultValue, rest];
  }

  const vals = value.jsonArray.map((val: string) => {
    return JSON.parse(val);
  }) as unknown as T[];

  return [vals, rest];
}
